import { useState } from 'react';
import {
  useAdvanceDeployment,
  useApproveDeployment,
  useDeployments,
  useOpsSettings,
  useVersions,
} from '@/services/queries';
import { useAppSelector } from '@/store/hooks';
import { canRelease, type Deployment, type DeploymentListQuery } from '@/types';
import { Badge } from '@/shared/ui/badge';
import { StatusPill } from '@/shared/ui/status-pill';
import { Button } from '@/shared/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Skeleton } from '@/shared/ui/skeleton';
import {
  Table,
  TableBody,
  TableCard,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/ui/tooltip';
import { ErrorState, Pagination } from '@/components/common';
import { formatDateTime } from '@/shared/lib/format';
import { stageLabel, humanise } from '@/lib/format';
import { StartDeploymentDialog } from './StartDeploymentDialog';

const PAGE_SIZE = 20;
const ANY = 'any';

const STAGES = [
  'queued',
  'building',
  'signing',
  'uploading',
  'smoke_testing',
  'rolled_out',
  'rolled_back',
  'failed',
] as const;

/**
 * Stages a deployment can still move to.
 *
 * The three terminal ones are ends: a deployment that has rolled out, rolled
 * back or failed is finished, and re-opening it would lose the record of what
 * actually happened. Deploy again instead.
 */
const TERMINAL: readonly string[] = ['rolled_out', 'rolled_back', 'failed'];

/** Enough to name the channel of every build a recent deployment points at. */
const VERSION_LOOKUP = { limit: 100, offset: 0 } as const;

export function DeploymentTable() {
  const [environment, setEnvironment] = useState<string>(ANY);
  const [stage, setStage] = useState<string>(ANY);
  const [offset, setOffset] = useState(0);

  const role = useAppSelector((state) => state.auth.user?.role);
  const userId = useAppSelector((state) => state.auth.user?.id);
  const mayDeploy = canRelease(role);

  /*
   * The second approval, mirrored from the server so the table can say why a
   * rollout is waiting. The server enforces it whatever this shows: moving an
   * unapproved stable production rollout to rolled out is refused with 409.
   */
  const settings = useOpsSettings();
  const versions = useVersions(VERSION_LOOKUP);
  const approve = useApproveDeployment();
  const channelOf = new Map(
    (versions.data?.data ?? []).map((version) => [version.id, version.releaseChannel]),
  );
  const waitsForApproval = (deployment: Deployment): boolean =>
    Boolean(settings.data?.requireSecondApproval) &&
    deployment.environment === 'production' &&
    channelOf.get(deployment.versionId) === 'stable' &&
    deployment.rollbackOf === null &&
    deployment.approvedBy === null;

  const query: DeploymentListQuery = {
    limit: PAGE_SIZE,
    offset,
    ...(environment !== ANY
      ? { environment: environment as DeploymentListQuery['environment'] }
      : {}),
    ...(stage !== ANY ? { stage: stage as DeploymentListQuery['stage'] } : {}),
  };

  const deployments = useDeployments(query);
  const advance = useAdvanceDeployment();

  const changeFilter = (setter: (value: string) => void) => (value: string) => {
    setter(value);
    setOffset(0);
  };

  const moveTo = (deployment: Deployment, next: string) => {
    advance.mutate({
      deploymentId: deployment.id,
      body: { stage: next as Deployment['stage'] },
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Select value={environment} onValueChange={changeFilter(setEnvironment)}>
          <SelectTrigger className="w-40" aria-label="Filter by environment">
            <SelectValue placeholder="Environment" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>All environments</SelectItem>
            <SelectItem value="staging">Staging</SelectItem>
            <SelectItem value="production">Production</SelectItem>
          </SelectContent>
        </Select>

        <Select value={stage} onValueChange={changeFilter(setStage)}>
          <SelectTrigger className="w-44" aria-label="Filter by stage">
            <SelectValue placeholder="Stage" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>All stages</SelectItem>
            {STAGES.map((value) => (
              <SelectItem key={value} value={value}>
                {stageLabel(value)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {mayDeploy ? (
          <StartDeploymentDialog />
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="ml-auto">
                <Button disabled>Start deployment</Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>Deploying requires superadmin or engineering.</TooltipContent>
          </Tooltip>
        )}
      </div>

      {deployments.isError && (
        <ErrorState
          title="Could not load deployments"
          description={deployments.error.message}
          action={
            <Button
              variant="outline"
              onClick={() => {
                void deployments.refetch();
              }}
            >
              Try again
            </Button>
          }
        />
      )}

      <TableCard>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-28">Environment</TableHead>
              <TableHead className="w-36">Stage</TableHead>
              <TableHead className="hidden lg:table-cell">Notes</TableHead>
              <TableHead className="hidden md:table-cell w-44">Started</TableHead>
              <TableHead className="w-48">Advance</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {deployments.isPending &&
              Array.from({ length: 5 }, (_, index) => (
                <TableRow key={index}>
                  <TableCell colSpan={5}>
                    <Skeleton className="h-5 w-full" />
                  </TableCell>
                </TableRow>
              ))}

            {deployments.data?.data.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="py-10 text-center text-muted-foreground">
                  No deployments match these filters.
                </TableCell>
              </TableRow>
            )}

            {deployments.data?.data.map((deployment) => {
              const finished = TERMINAL.includes(deployment.stage);
              const waiting = !finished && waitsForApproval(deployment);
              const ownRollout =
                deployment.initiatedBy !== null && deployment.initiatedBy === userId;

              return (
                <TableRow key={deployment.id}>
                  <TableCell>
                    <Badge
                      variant={deployment.environment === 'production' ? 'default' : 'secondary'}
                      className="font-normal"
                    >
                      {humanise(deployment.environment)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <StatusPill status={deployment.stage} minWidth={92}>
                      {stageLabel(deployment.stage)}
                    </StatusPill>
                    {deployment.rollbackOf && (
                      <p className="mt-1 text-xs text-muted-foreground">Rollback</p>
                    )}
                    {deployment.approvedAt && (
                      <p
                        className="mt-1 text-xs text-muted-foreground"
                        title={formatDateTime(deployment.approvedAt)}
                      >
                        Second approval given
                      </p>
                    )}
                  </TableCell>
                  <TableCell className="hidden lg:table-cell max-w-sm">
                    <span className="line-clamp-2 text-sm text-muted-foreground">
                      {deployment.notes ?? '—'}
                    </span>
                  </TableCell>
                  <TableCell className="hidden md:table-cell text-muted-foreground tabular-nums">
                    {formatDateTime(deployment.startedAt)}
                  </TableCell>
                  <TableCell>
                    {finished ? (
                      <span className="text-sm text-muted-foreground">
                        {deployment.completedAt ? formatDateTime(deployment.completedAt) : 'Closed'}
                      </span>
                    ) : (
                      <div className="space-y-1.5">
                        {waiting && (
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-semibold text-amber-600">
                              Needs second approval
                            </span>
                            <Button
                              size="sm"
                              variant="outline"
                              className="ml-auto h-7"
                              disabled={!mayDeploy || ownRollout || approve.isPending}
                              title={
                                ownRollout
                                  ? 'Someone other than the person who started it must approve'
                                  : undefined
                              }
                              onClick={() => {
                                approve.mutate(deployment.id);
                              }}
                            >
                              Approve
                            </Button>
                          </div>
                        )}
                        <Select
                          value=""
                          onValueChange={(value) => {
                            moveTo(deployment, value);
                          }}
                          disabled={!mayDeploy || advance.isPending}
                        >
                          <SelectTrigger
                            className="h-8 w-full"
                            aria-label={`Advance ${deployment.environment} deployment`}
                          >
                            <SelectValue placeholder="Move to…" />
                          </SelectTrigger>
                          <SelectContent>
                            {STAGES.filter(
                              (value) =>
                                value !== deployment.stage && !(waiting && value === 'rolled_out'),
                            ).map((value) => (
                              <SelectItem key={value} value={value}>
                                {stageLabel(value)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableCard>

      {deployments.data && (
        <Pagination
          offset={offset}
          pageSize={PAGE_SIZE}
          count={deployments.data.data.length}
          total={deployments.data.meta.total}
          hasMore={deployments.data.meta.hasMore}
          onOffsetChange={setOffset}
        />
      )}
    </div>
  );
}
